import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  ActivationStart,
  NavigationStart,
  Router,
  RouterOutlet,
} from "@angular/router";
import { DomSanitizer } from "@angular/platform-browser";
import { MatIconRegistry } from "@angular/material/icon";
import { TranslocoService } from "@jsverse/transloco";
import "firebase/auth";
import "firebase/messaging";
import "firebase/compat/auth";
import "firebase/compat/messaging";
import { TourService } from "ngx-ui-tour-md-menu";

import { LOREN_ROUTER_OUTLETS } from "./shared/constants/loren-router-outlets.constants";
import { LOREN_ROUTES } from "./shared/constants/loren-routes.constants";
import { LorenTourStep } from "./shared/interfaces/loren-tour-step.interface";
import {
  LorenNavService
} from "./shared/modules/loren-nav/components/loren-nav/services/loren-nav-service/loren-nav.service";
import {
  LorenAuthenticationService
} from "./shared/services/loren-authentication-service/loren-authentication.service";
import { LorenUtilsService } from "./shared/services/loren-utils-service/loren-utils.service";
import { LorenMessagingService } from "./shared/services/loren-messaging-service/loren-messaging.service";
import { LorenTourService } from "./shared/services/loren-tour-service/loren-tour.service";
import {
  LorenBreakpointObserverService
} from "./shared/services/loren-breakpoint-observer-service/loren-breakpoint-observer.service";
import {
  LorenErrorHandlingService
} from "./shared/services/loren-error-handling-service/loren-error-handling-service.service";
import { LorenError } from "./shared/services/loren-error-handling-service/loren-error.enum";
import { DialogService } from "./common/dialog/dialog.service";

@Component({
  selector: "loren-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit {
  tourService = inject(TourService);
  lorenTourService = inject(LorenTourService);
  private breakpointObserverService = inject(LorenBreakpointObserverService);
  private matIconRegistry = inject(MatIconRegistry);
  private router = inject(Router);
  private translateService = inject(TranslocoService);
  private lorenAuthenticationService = inject(LorenAuthenticationService);
  private lorenMessagingService = inject(LorenMessagingService);
  private lorenNavService = inject(LorenNavService);
  lorenErrorHandlingService = inject(LorenErrorHandlingService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly dialogService = inject(DialogService);

  readonly title = "loren";
  readonly errors = LorenError;

  isUserLoggedIn = false;
  hasUserPassedTour = LorenUtilsService.hasUserPassedTour();
  private welcomeTourPrompted = false;

  @ViewChild(RouterOutlet) loginOutlet: RouterOutlet;
  @ViewChild("ro") detailsOutlet: RouterOutlet;
  @ViewChild("contentWrapper") contentWrapper: ElementRef;
  isHandset: boolean;

  ngOnInit(): void {
    this.isUserLoggedIn = this.lorenAuthenticationService.isAuthenticated();
    this.maybePromptWelcomeTour();
    this.breakpointObserverService
      .getHandsetStateSubscription()
      .subscribe((x) => (this.isHandset = x));
    this.lorenAuthenticationService
      .getUserInfoSubscription()
      .subscribe((res) => {
        this.isUserLoggedIn = res !== null;

        if (this.isUserLoggedIn) {
          this.maybePromptWelcomeTour();
          this.lorenMessagingService.requestPermission();
        }
      });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.lorenErrorHandlingService.setSuccess();
      }
      if (
        event instanceof ActivationStart &&
        event.snapshot.outlet === LOREN_ROUTER_OUTLETS.DETAILS
      ) {
        this.detailsOutlet.deactivate();
      }

      if (
        event instanceof ActivationStart &&
        event.snapshot.outlet === "login-router-outlet"
      ) {
        this.loginOutlet.deactivate();
      }
    });

    this.prepareCustomIcons();

    const lastSelectedLanguage = LorenUtilsService.getLastSelectedLanguage();
    const lang = !lastSelectedLanguage
      ? this.translateService.getActiveLang()
      : lastSelectedLanguage;
    LorenUtilsService.setMomentLanguageLocale(lang);

    window.addEventListener("appUrlOpen", (event) => {
      const notificationId = event?.["notificationId"];
      if (!notificationId) {
        return;
      }

      const path = `${LOREN_ROUTES.NOTIFICATIONS}/${notificationId}/${LOREN_ROUTES.DETAILS}`;
      if (path) {
        this.router.navigate([path]);
      }
    });
  }

  ngAfterViewInit(): void {
    this.handleTourSteps();
    this.handleContentWrapperHeight();
  }

  handleTourEnd() {
    this.tourService.end();
  }

  handleTourBack() {
    this.tourService.prev();
  }

  handleTourNext() {
    this.tourService.next();
  }

  handleCancelTour() {
    this.hasUserPassedTour = true;
    LorenUtilsService.updateLastUserTour();
  }

  private maybePromptWelcomeTour() {
    if (!this.isUserLoggedIn || this.hasUserPassedTour || this.welcomeTourPrompted) {
      return;
    }
    this.welcomeTourPrompted = true;

    this.dialogService
      .confirm(
        {
          title: this.translateService.selectTranslate("tour.welcome_dialog_title"),
          message: this.translateService.selectTranslate("tour.welcome_dialog_sub_title"),
          confirmLabel: this.translateService.selectTranslate("tour.proceed_tour"),
          cancelLabel: this.translateService.selectTranslate("tour.cancel_tour"),
        },
        { hasBackdrop: true, disableClose: true }
      )
      .subscribe((proceed) => {
        if (proceed) {
          this.lorenTourService.triggerCommonApplicationTour();
        } else {
          this.handleCancelTour();
        }
      });
  }

  handleDetailsOutletActivated(outlet: RouterOutlet): void {
    if (outlet.name === LOREN_ROUTER_OUTLETS.LOGIN) {
      outlet.deactivate();
    }
  }

  handleContentWrapperHeight() {
    const toolbarMobileSize = 56;
    const toolbarNormalSize = 64;
    const subtractFromHeight = this.isHandset
      ? 2 * toolbarMobileSize
      : 2 * toolbarNormalSize;

    // Margin coming from .loren-page-content-container
    // This is a dirty fix - would need to be fixed properly later
    // TODO: Implement the dynamic height properly
    const marginY = 36;

    this.breakpointObserverService.setContentWrapperHeight(
      window.innerHeight - subtractFromHeight - marginY * 2
    );
  }

  private prepareCustomIcons() {
    this.matIconRegistry.addSvgIconResolver((name) => {
      return this.sanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`);
    });
  }

  private handleTourSteps() {
    let lastStep: LorenTourStep;
    // TODO: fix
    this.tourService.stepShow$.subscribe((step) => {
      const eventStep = step.step;
      if (!this.isHandset) {
        return;
      }

      if (eventStep === lastStep) {
        return;
      }

      const currentStep = this.tourService.currentStep as LorenTourStep;

      if (lastStep && currentStep.hideMobileNav !== lastStep.hideMobileNav) {
        this.tourService.pause();
        this.lorenNavService.toggleSidenav(!currentStep.hideMobileNav);
        setTimeout(() => this.tourService.resume(), 500);
      }

      lastStep = eventStep;
    });

    this.tourService.end$.subscribe(() => {
      this.lorenTourService.updateTourRunningState(false);
    });
  }
}
